<template>

    <div>
        <div class="row align-items-center">
            <div class="col-xl-2  mb-2">
                <h1>{{ $t("attendance-report") }}</h1>
            </div>
            <div class="col-xl-10 mb-2">
                <div class="d-flex flex-wrap align-items-end justify-content-xl-end" style="gap: 0 2rem">
                    <!-- Date filters -->
                    <div class="d-flex flex-nowrap align-items-end" style="gap: 1rem">
                        <DateInput
                            class="mb-0"
                            :label="$t('from')"
                            v-model="filters.datetime_from"
                            @input="value => refresh({ datetime_from: value })"
                        />
                        <DateInput
                            class="mb-0"
                            :label="$t('to')"
                            v-model="filters.datetime_to"
                            @input="value => refresh({ datetime_to: value })"
                        />
                    </div>
                    <!-- Status filters + actions -->
                    <div class="d-flex flex-nowrap align-items-center" style="gap: 0.4rem">
                        <div class="btn-group">
                            <button class="btn btn-on-good"
                                :class="{
                                    'btn-primary': filters.attended,
                                    'btn-outline-secondary': !filters.attended
                                }" @click="filters.attended=!filters.attended; fetchReport();">
                                {{ $t('attended') }}
                            </button>
                            <button class="btn btn-on-good"
                                :class="{
                                    'btn-primary': filters.absent,
                                    'btn-outline-secondary': !filters.absent
                                }" @click="filters.absent=!filters.absent; fetchReport();">
                                {{ $t('absent') }}
                            </button>
                            <button v-if="!coalesce" class="btn btn-on-good"
                                :class="{
                                    'btn-primary': filters.unknown,
                                    'btn-outline-secondary': !filters.unknown
                                }" @click="filters.unknown=!filters.unknown; fetchReport();">
                                {{ $t('unknown') }}
                            </button>
                        </div>
                        <div class="form-check mb-0 ml-1">
                            <input class="form-check-input" type="checkbox" id="coalesceCheck" v-model="coalesce" @change="onCoalesceChange">
                            <label class="form-check-label" for="coalesceCheck">{{ $t('coalesce') }}</label>
                        </div>
                        <button class="btn btn-sm btn-outline-primary ml-1" :title="$t('advanced-search')"
                            @click="showadvancedsearch = !showadvancedsearch">
                            <b-icon-search />
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" :title="$t('download')" @click="getCSV()">
                            <b-icon-download v-if="!downloading" />
                            <b-spinner v-else small/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Advanced search -->
        <div v-if="showadvancedsearch">
			<div class="row">
				<div class="col-md-2">
					<SelectInput
						v-model="filters.programme"
						@input="value =>refresh({ programme: value })"
						:label="$t('programme')"
						:options="$config.getValue('programmes')"
						optionLabel="Name"
						:nullable="true"
						:nullLabel="$t('all')"
					/>
				</div>
				<div class="col-md-2">
					<div class="form-group">
						<label>{{ $t("year") }}</label>
						<select 
                            class="form-control" 
                            v-model="filters.year" 
                            @change="refresh" 
                            :aria-label="$t('year')"
                        >
							<option :value="null">{{ $t("any") }}</option>
							<template v-if="filters.programme">
								<option
								v-for="year in parseInt(filters.programme.CourseLengthYears)"
								:key="year"
								:value="year.toString()"
								>{{ year }}
								</option>
								<option :value="parseInt(filters.programme.CourseLengthYears) + 1">{{ $t('graduated') }}</option>
							</template>
						</select>
					</div>
				</div>
				<div class="col-md-2">
					<SelectInput
						v-model="filters.group"
						@input="value => refresh({ group: value })"
						:label="$t('group')"
						:options="$config.getValue('groups')"
						:indexIsValue="true"
						:nullable="true"
						:nullLabel="$t('all')"
					/>
				</div>
                <div class="col-md-3">
                    <SelectSearchInput
                        :label="$t('type')"
                        v-model="filters.ttsessiontype_id"
                        :options="this.ttsessiontypes"
                        optionValue="id"
                        optionLabel="name"
                        @input="value => refresh({ ttsessiontype_id: value })"
                        :nullable="true"
                    />

                </div>
                <div class="col-md-3">
                    <SelectSearchInput
                        :label="$t('room')"
                        v-model="filters.ttroom_id"
                        :options="this.ttrooms"
                        optionValue="id"
                        optionLabel="name"
                        @input="value => refresh({ ttroom_id: value })"
                        :nullable="true"
                    />

                </div>
			</div>
            <div class="row align-items-center">
                <div class="col-md-10">
                    <div class="form-group">
						<label>{{ $t("students") }}</label>
						<div
							class="pseudo-input mb-3"
						>
							<input
								type="search"
								class="input-bare w-full flex-1"
								v-if="!filters.students.length"
								@click="selectStudent"
								@keyup.enter="selectStudent"
							/>
							<span
								class="badge badge-secondary badge-sm mr-2 mb-2"
								v-for="(student, index) in filters.students"
								v-bind:key="index"
							>
								{{ student.displayName }}
								<a @click="removeStudent(student)"><b-icon icon="x"></b-icon></a>
							</span>
							<span
								class="badge badge-primary badge-sm mr-2 mb-2"
								v-if="filters.students.length"
								@click="selectStudent"
								@keyup.enter="selectStudent"
								tabindex="0"
								:aria-label="$t('student') + ' ' + $t('add')"
							>
								<b-icon icon="plus"></b-icon> {{ $t("add") }}
							</span>
							<span
								class="badge badge-danger badge-sm mr-2 mb-2"
								v-if="filters.students.length"
								@click="filters.students=[];fetchReport()"
								@keyup.enter="filters.students=[];fetchReport()"
								tabindex="0"
								:aria-label="$t('student') + ' ' + $t('clear-all')"
							>
								<b-icon icon="x"></b-icon> {{ $t('clear-all') }}
							</span>
						</div>
					</div>
                </div>
                <div class="col-md-2 mb-3">
                    <YesNoToggle
                        v-model="filters.isinternational"
                        class="mr-auto my-0"
                        :yesLabel="$t('yes')"
                        :noLabel="$t('no')"
                        :noFirst="true"
                        :label="$t('international-student')"
                        @input="value => refresh({ isinternational: value })"
                    />
                     <!--CheckboxInput
                        :label="$t('international-student')"
                        :value="search.isinternational"
                        @input="value => {search.isinternational = value; fetchReport()}"
                    /-->
                </div>
            </div>
		</div>

        <div class="row">
            <div class="col-12">
                <Table
                    :loading="loading"
                    :submitting="submitting"
                    :cols="[
                        { key: 'datetimeformatted', heading: $t('date-time'), size: 2 },
                        { cb: this.getSession, key: 'session', heading: $t('event'), size: 3 },
                        { cb: this.getStudent, key: 'student', heading: $t('student'), size: 2},
                        { key: 'attendancelabel', heading: $t('attendance'), size: 1},
                        { key: 'source_text', heading: $t('source'), size: 2,
                            icon: (entry) => {
                                if(entry.formresponse_id) return { icon: 'box-arrow-up-right', variant: 'info' };
                                return null;
                            }
                        },
                    ]"
                    :data="report"
                    breakpoint="md"
                    @selectItem="selectItem"
                    :filters="{
                        ...filters,
                        page: filters.page,
                        page_size: filters.page_size,
                        total_count: total_count
                    }"
                    :scrollPaginate="true"
                    @refresh="refresh"
                    @toPage="toPage"
                >
                <template v-slot:rowActions="report" v-if="canEditAttendance">
                    <div>

                        <div class="btn-group" style="display: block">
                            <button
                                class="btn btn-on-good"
                                v-bind:class="{
                                    'btn-on': report.item.attendance == 1,
                                    'btn-off': report.item.attendance !== 1
                                }"
                                @click.stop="updateAttendance(report.item, true)"
                            >
                                {{ $t('here') }}
                            </button>
                            <button
                                class="btn btn-on-bad"
                                v-bind:class="{
                                    'btn-on': report.item.attendance == -1,
                                    'btn-off': report.item.attendance !== -1
                                }"
                                @click.stop="updateAttendance(report.item, false)"

                            >
                                {{ $t('absent') }} /
                                {{ $t('late') }}
                            </button>
                        </div>

                    </div>
                </template>
                </Table>
            </div>

        </div>
    </div>
</template>

<script>
import Table from "src/components/Table/Table.vue";
import Papa from 'papaparse';
import DateInput from "src/components/Inputs/DateInput";
import YesNoToggle from "src/components/Inputs/YesNoToggle.vue";
import SessionManager from "src/modules/session/sessionManager";


export default {
    components: {
        Table,
        DateInput,
        YesNoToggle
    },
    data() {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 31);

        const future = new Date(today);
        future.setDate(today.getDate()+7);

        return {
            report:[],
            total_count: 0,
            loading: true,
            submitting: false,
            showadvancedsearch:false,
            students:[],
            ready:false,
            ttsessiontypes:[],
            ttrooms:[],
            filters: {
                page: 1,
                page_size: 30,
                search: '',
                order: '',
                programme: '',
                year: '',
                attended: false,
                absent: true,
                unknown: true,
                students: [],
                datetime_from: past.toISOString().split('T')[0],
                datetime_to: future.toISOString().split('T')[0]
            },
            coalesce: false,
            downloading: false,
            sessionManager: new SessionManager(this.$route)
            //item:{}
        }
    },
    mounted() {
        const savedFilters = this.sessionManager.get('filters')
        if (savedFilters) this.filters = savedFilters

        const savedCoalesce = this.sessionManager.get('coalesce')
        if (savedCoalesce !== null) this.coalesce = savedCoalesce

        const savedShowAdvanced = this.sessionManager.get('showadvancedsearch')
        if (savedShowAdvanced !== null) this.showadvancedsearch = savedShowAdvanced

        this.fetchReport()
        this.getStudents()
        this.getTTSessionTypes()
        this.getRooms()
    },
    methods: {
        fetchReport(silent = false) {
            if (this.submitting) return
            this.submitting = true

            this.sessionManager.set('filters', this.filters)
            this.sessionManager.set('coalesce', this.coalesce)
            this.sessionManager.set('showadvancedsearch', this.showadvancedsearch)

            return this.$cfetch(`${this.$settings.APIBaseURL}/attendance/report`, {
                method: 'post',
                data: this.filters,
            }).then((res) => {

                if (res.status !== 'SUCCESS') return
				res.result.students.map((x) => {
					if(x.ttevent_datetimefrom){
						x.datetimeformatted = this.$datetime.getLocaleStringFromISODate(x.ttevent_datetimefrom) + ' ' + this.$datetime.isoDateTimeToTime(x.ttevent_datetimefrom);
					} else {
						x.datetimeformatted = '';
					}
					if(x.attendance == 1){
						x.attendancelabel = this.$t('attended');
					} else if(x.attendance == -1){
						x.attendancelabel = `${this.$t('absent')} / ${this.$t('late')}`;
					} else {
						x.attendancelabel = this.coalesce
							? `${this.$t('absent')} / ${this.$t('late')}`
							: this.$t('unknown');
					}
					if(x.teacher_id){
						x.source_text = `${this.$t('manual-entry')} - ${x.teacher_name}`;
					}
					if(x.formresponse_id){
						x.source_text = this.$t('form-submission');
					}
					if(x.ttcheckincode_code){
						x.source_text = `${this.$t('qr-code')} - ${x.ttcheckincode_code}`;
					}
				});

                this.filters.page = res.pagination.page
                this.filters.page_size = res.pagination.page_size
                this.filters.total = res.pagination.total_count

                if(!silent) this.report = res.result.students

                return res.result.students
                //this.handleReport(res.results.students)


            }).catch((err) => {
                this.$log.error.call(this, err)
                console.log(err)
            }).finally(() => {
                this.submitting = false
                this.loading = false

            })
        },
        updateAttendance(student , status) {
            this.$cfetch(`${this.$settings.APIBaseURL}/attendance/register/saveattendance`, {
                data: {
                    'ttevent_id': Number(student.ttevent_id),
                    'student_id': student.user_id,
                    'status': status,
                },
            }).then((res) => {
                if (res.status === "SUCCESS") {
                    if(status){
                        student.attendance = 1;
                        student.attendancelabel = this.$t('attended');
                    } else {
                        student.attendance = -1;
                        student.attendancelabel = `${this.$t('absent')} / ${this.$t('late')}`;
                    }
                } else {
                    this.somethingWentWrong();
                }
            }).catch(() => {
                this.somethingWentWrong();
            });
		},
        somethingWentWrong(){
			this.$store.commit("openDialog", {
				cancelBtn: true,
				title: this.$t("oops"),
				content: this.$t("something-went-wrong")
			});
		},
        onCoalesceChange() {
            this.fetchReport();
        },
        refresh(filters) {
            this.filters = { ...this.filters, ...filters, page: 1 }
            this.fetchReport()
        },
        toPage(pageNo) {
            this.filters.page = pageNo
            this.fetchReport()
        },
        handleReport(){
            this.filters.fetch_all = true;

            return this.fetchReport(true).then((res) => {
                if (!res) throw new Error('Report fetch failed');
                return res.map(user => {
                    //Format keys for CSV
                    let csvCols = {
                        "ttevent_id": "Event ID",
                        "ttevent_datetimefrom" : "From",
                        "ttevent_datetimeto": "To",
                        "ttsessiontype_name": "Session Type",
                        "ttroom_name": "Room",
                        "ttevent_title": "Event Title",
                        "student_id":"Student ID",
                        "displayname" : "Student Name",
                        "attendancelabel": "Attendance",
                        "source_text": "Source",
                        "absencetype_title": "Absence Type",
                        "student_year": "Year"
                    }

                    if(this.$UserSession.hasPriv('REPORT_EMAILADDRESS')) csvCols = {...csvCols, "email" : "Email"}

                    const formattedUser = {}

                    for (const prop in csvCols) {// rename key if needed
                        formattedUser[csvCols[prop]] = user[prop];
                    }

                // eslint-disable-next-line no-unused-vars
                    let {  absencetype_title, ...rest } = formattedUser;

                    if (this.filters.absent) return formattedUser;

                    return rest;
                });
            }).finally(() => {
                this.filters.fetch_all = false;
            });
        },
        getCSV() {
            this.downloading = true;
            return this.handleReport().then(report => {
                // Convert to CSV
                const csv = Papa.unparse(report);

                // Create a Blob from the CSV string
                const blob = new Blob([csv], { type: 'text/csv' });

                // Give it a download link
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `attendance_report.csv`;

                // Trigger download
                a.click();

                // Housekeeping
                URL.revokeObjectURL(url);

                return csv;
            }).catch(error => {
                console.error('Error generating CSV:', error);
                this.somethingWentWrong();
            }).finally(() => this.downloading = false);
        },
        // getCSV() {
        //     let report 
        //     this.handleReport().then((res) =>{
        //         report = res
        //     })
        //     // Get the CSV via a Promise
        //     return new Promise((resolve, reject) => {
        //         try {
        //             const csv = Papa.unparse(report);
        //             resolve(csv);
        //         } catch (error) {
        //             console.error('Error unparsing CSV file:', error);
        //             reject(error);
        //         }
        //     }).then(res => {
        //         // Create a Blob from the CSV string
        //         const blob = new Blob([res], { type: 'text/csv' });

        //         // Give it a download link
        //         const url = URL.createObjectURL(blob);
        //         const a = document.createElement('a');
        //         a.href = url;
        //         a.download = `attendance_report.csv`;

        //         // Trigger download
        //         a.click();

        //         // Housekeeping
        //         URL.revokeObjectURL(url);
        //     });
        // },
        getTitle(id,isTopic){
            if(!this.loading && id && this.topiccomponenttypes  && this.topics){

                let matchingObject ={}

                matchingObject  = this.topiccomponenttypes.find( item => item.id == id);

                if(isTopic ) matchingObject = this.topics.find( item => item.id == id);

                return matchingObject.title;
            }
            return ;
        },
        selectItem(item){
        	this.$router.push(`/attendance/register/${item.ttevent_id}`);
        },
        getStudents(){
            this.$users.getStudents.call(this).then(students =>{
                this.students = students.map(user => {
                        return {
                            ...user,
                            displayname: user.displayName
                        }
                    });
                this.ready = true
            });
        },
        getTTSessionTypes() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/sessiontypes/all`, {
                data: {
                },
            }).then((res) => {
                if (res.status === "SUCCESS") {
                    this.ttsessiontypes= res.results;
                } else {
                    this.somethingWentWrong();
                }
            }).catch(() => {
                this.somethingWentWrong();
            });
        },
        getRooms () {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/ttroom/listall`).then(
                (res) => {
                    if (res.status === "SUCCESS") {
                        this.ttrooms = res.results;
                    } else {
					this.$log.info.call(this, status)
				    }
                })
            .catch(error => {
                this.$log.error.call(this, error);
            });
		},
        removeStudent(selection) {
			const self = this;
			self.filters.students.find((student, i) => {
				if (student.id === selection.id) {
					self.$delete(self.filters.students, i);
					self.fetchReport();
					return true;
				}
			});
			return false;
		},
        selectStudent() {
			const self = this;
			this.$users.getStudents.call(this).then((students) => {
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t("select-a-student"),
					selectCallback: function (selection) {
						const isSelected = self.filters.students.find((student, i) => {
							if (student.id === selection.id) {
								self.$delete(self.filters.students, i);
								return true;
							}
						});
						if (!isSelected) {
							self.filters.students.push(selection);
						}
						self.fetchReport();
					},
					selected: this.filters.students,
					data: students,
					captionKey: "displayName",
                    subcaptionKey: "studentid",
					studentSearchFilters: true,
					selectAllBtn: true
				});
			});
		},

		getSession(item){
			return item.ttsessiontype_name + ' | ' + item.ttroom_name;
		},
		getStudent(item){
			return item.student_id + ' ' + item.displayname;
		},
       

    },
    computed: {
        canEditAttendance(){
        	return this.$UserSession.hasPriv.call(this, 'TIMETABLE_ADMIN') || this.$UserSession.hasPriv('PLACEMENT_MY_PLACEMENTS') || (this.$UserSession.hasPriv.call(this, 'TIMETABLE_ATTENDANCE'))
        },


	}

}
</script>
